import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Card, CardContent, CardActions, Button } from '@material-ui/core';
import { actions as genericActions, EntityName, selectors as genericSelectors } from '../../redux/reducers/generic';
import { selectors as pendingSelectors } from '../../redux/reducers/pendingOperations';
import { ErrorPayload, selectors as errorSelectors } from '../../redux/reducers/errors';
import { Typobold } from '../typobold';
import FormSubmit from '../form/formSubmit';
import { TooltipButton } from '../tooltip-button';
import GlobalLoader from '../main-loading/global-loader';
import ErrorMessage from '../error-message/error-message';
import FormTextArea from '../form/formTextArea';

interface CommentOwnProps {
    entity: EntityName;
    entityId: string;
}

interface CommentReduxProps extends WithTranslation {
    comment: string;
    isLoadingComment: boolean;
    genericError: ErrorPayload;
    getComment: (entityName: EntityName, entityId: string, callback) => void;
    saveComment: (entityName: EntityName, entityId: string, payload) => void;
}

type CommentProps = CommentOwnProps & CommentReduxProps;

const Comment = (props: CommentProps): JSX.Element => {
    const classes = useStyles();

    const {
        t,
        entity,
        entityId,
        comment: initialComment,
        isLoadingComment,
        genericError,
        getComment,
        saveComment,
    } = props;

    const [comment, setComment] = React.useState<string>(initialComment || '');

    useEffect(() => {
        if (entity && entityId && !initialComment) {
            getComment(entity, entityId, (data) => {
                setComment(data);
            });
        }
        return () => {};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entity, entityId]);

    if (!entity || !entityId) {
        return <p>{'Missing entity or entityId'}</p>;
    }

    const handleChange = (e) => {
        setComment(e.target.value);
    };

    const handleSubmit = () => {
        const payload = {
            comment,
        };
        saveComment(entity, entityId, payload);
    };

    return (
        <>
            <br />
            <Typobold>{t('common:intern_note')}</Typobold>
            <br />
            <ErrorMessage payload={genericError} />
            <FormSubmit debounceSubmit={{ callback: handleSubmit }} stopPropagation={true}>
                <Card className={classes.card}>
                    {isLoadingComment ? (
                        <GlobalLoader startLoadingTimeout={0} />
                    ) : (
                        <>
                            <CardContent className={classes.cardContent}>
                                <FormTextArea
                                    id="comment"
                                    key="comment"
                                    name="comment"
                                    placeholder={t('common:form.additional_infos')}
                                    value={comment}
                                    label={t(`common:form.comment`)}
                                    required={true}
                                    disabled={false}
                                    handleChange={handleChange}
                                />
                            </CardContent>
                            <CardActions className={classes.rightActions}>
                                <TooltipButton title={t('common:save')}>
                                    <Button size="small" type="submit" variant="contained" color="secondary">
                                        {t('common:save')}
                                    </Button>
                                </TooltipButton>
                            </CardActions>
                        </>
                    )}
                </Card>
            </FormSubmit>
        </>
    );
};

//=====================================//
//=============== Style ===============//
//=====================================//

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        card: {
            padding: 16,
        },
        cardContent: {
            display: 'flex',
            flex: 1,
            flexWrap: 'wrap',
            padding: 0,
        },
        rightActions: {
            justifyContent: 'flex-end',
        },
    }),
);

const mapStateToProps = (state, ownProps: CommentOwnProps) => ({
    comment: genericSelectors.entityComment(state, ownProps.entity, ownProps.entityId),
    genericError: errorSelectors.genericError(state),
    isLoadingComment: pendingSelectors.isLoadingComment(state),
});

const mapDispatchToProps = {
    getComment: genericActions.getComment,
    saveComment: genericActions.saveComment,
};

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation('action_log')(Comment));
